import React from 'react';

import { CircularProgress } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';

import { OptionEntry } from '../../utils/options';

const getValueWithCount = (
  name: string,
  count: number | null = null,
  size = 14,
  marginLeft = 10,
): JSX.Element => (
  <>
    {name}
    {count !== null ? (
      ` (${count})`
    ) : (
      <CircularProgress color='inherit' size={size} style={{ marginLeft }} />
    )}
  </>
);

interface Props {
  options: OptionEntry[];
  allOption?: boolean;
  allOptionCount?: number;
  value: string;
  onChange: (key?: string) => void;
  styles?: React.CSSProperties;
  containerStyles?: React.CSSProperties;
}

export const OptionsBar: React.FC<Props> = ({
  onChange,
  options,
  allOption,
  value,
  allOptionCount,
  styles,
  containerStyles,
}) => {
  const handleOnClick = React.useCallback((val: string | undefined) => onChange(val), [onChange]);

  return (
    <Container style={{ ...containerStyles }}>
      <StyledToggleButtonGroup>
        {allOption && (
          <ToggleButton
            selected={!value}
            value={''}
            onClick={() => handleOnClick(undefined)}
            size='small'
            sx={{
              fontSize: '15px',
              fontFamily: 'Poppins',
              textTransform: 'capitalize',
              fontWeight: !value ? 500 : 400,
              color: '#757575',
              ...styles,
            }}
          >
            {allOptionCount ? getValueWithCount('All', allOptionCount) : 'All'}
          </ToggleButton>
        )}
        {options.map((pair) => (
          <ToggleButton
            key={pair.value}
            selected={pair.value === value}
            value={pair.value}
            onClick={() => handleOnClick(pair.value)}
            size='small'
            sx={{
              textTransform: 'capitalize',
              color: '#757575',
              fontWeight: pair.value === value ? 500 : 400,
              fontSize: '15px',
              fontFamily: 'Poppins',
              ...styles,
            }}
            disabled={pair?.disabled}
          >
            {pair.label}
          </ToggleButton>
        ))}
      </StyledToggleButtonGroup>
    </Container>
  );
};

const Container = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flexFlow: 'row nowrap',
  borderRadius: '6px',
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButton-root': {
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderColor: '#F1F2F1',
    whiteSpace: 'nowrap',
    height: '40px',
    '@media (max-width: 475px)': {
      color: '#848484',
      fontSize: '12px',
      fontWeight: 400,
      wordWrap: 'break-word',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
    '&.Mui-selected': {
      background: '#f6f6f6',
      color: '#232323',
      '@media (max-width: 475px)': {
        color: '#111111',
        fontSize: '12px',
        fontFamily: 'Poppins',
        fontWeight: 500,
        wordWrap: 'break-word',
      },
    },
    '&:hover': {
      background: '#f6f6f6',
    },
  },
}));
